/**
 * Module-level "is an interruption-sensitive operation running?" store — the single source
 * of truth shared by the reload gate, the `beforeunload` guard, and the in-app navigation
 * guards. ANY long/critical operation that must not be interrupted by a reload or navigation
 * — a bulk upload, a bulk delete, etc. — brackets itself with `begin()`/`end()`.
 *
 * It is a plain module singleton precisely because the API client is constructed before
 * React renders and therefore cannot read a React Context. A counter (not a boolean) so
 * concurrent operations compose correctly.
 */

let activeCount = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export const busyTracker = {
  /** Mark one interruption-sensitive operation as started. Pair with `end()` (try/finally). */
  begin(): void {
    activeCount += 1;
    emit();
  },

  /** Mark one operation as finished (success or failure). */
  end(): void {
    activeCount = Math.max(0, activeCount - 1);
    emit();
  },

  /** True while at least one such operation is in flight. */
  isActive(): boolean {
    return activeCount > 0;
  },

  /** Subscribe to changes. Returns an unsubscribe fn. Shape fits `useSyncExternalStore`. */
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /** Current boolean snapshot. Shape fits `useSyncExternalStore`. */
  getSnapshot(): boolean {
    return activeCount > 0;
  },
};
